import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import clinicAPI from "../../../services/clinicApi";
import { normalizeRole } from "../../../utils/roles";
import { CLUB_LIFE_CATEGORIES } from "../../../constants/clubLifeCategories";
import ClubLifePostCard from "../common/ClubLifePostCard";
import ClubPhotoSlider from "../common/ClubPhotoSlider";
import ClubLifeComposer from "../PatientPortal/ClubLifeComposer";
import AdminClubEditSheet from "./AdminClubEditSheet";
import { useClubLifeInteractions } from "../../../hooks/useClubLifeInteractions";
import { useAdminRefresh } from "../../../hooks/useAdminRefresh";
import useClubPortalBranding from "../../../hooks/useClubPortalBranding";
import { useIsMobile } from "../../../hooks/useMediaQuery";
import { mediaUrl } from "../../../utils/clientHeroTheme";
import { clubGalleryPhotos } from "../../../utils/clubGalleryPhotos";
import { membersCountLabel } from "../../../utils/ruPlural";
import { updateTenant } from "../../../store/authSlice";
import s from "../PatientPortal/clientApp.module.css";
import aa from "../AdminPortal/adminApp.module.css";
import md from "../common/manageDesktop.module.css";
import { Avatar, Skeleton, useToast } from "../../ui";

const FILTERS = CLUB_LIFE_CATEGORIES.filter((c) =>
  ["all", "achievement", "event", "announcement"].includes(c.id),
);

function PinIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M12 21s7-4.35 7-10a7 7 0 10-14 0c0 5.65 7 10 7 10z"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinejoin="round"
      />
      <circle cx="12" cy="11" r="2.25" stroke="currentColor" strokeWidth="1.75" />
    </svg>
  );
}

function PhoneIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M6.5 4h3l1.5 4-2 1.25a11 11 0 005.75 5.75L17 13l4 1.5v3A2 2 0 0119 20C10.16 20 4 13.84 4 5a2 2 0 012-1z"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ChatIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M21 11.5a8.5 8.5 0 0 1-8.5 8.5c-1.2 0-2.3-.25-3.3-.7L3 21l1.7-5.2A8.5 8.5 0 1 1 21 11.5Z"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function UsersIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2M9 11a4 4 0 100-8 4 4 0 000 8zm10 10v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function formatBranchLine(branch) {
  const parts = [branch.address, branch.city, branch.metro].filter(Boolean);
  return parts.join(" · ");
}

export default function AdminClubLife() {
  const toast = useToast();
  const isMobile = useIsMobile(768);
  const desktop = !isMobile;
  const dispatch = useDispatch();
  const authTenant = useSelector((state) => state.auth.tenant);
  const { profile, role } = useSelector((state) => state.auth);
  const userRole = normalizeRole(role);

  const canDeletePost = useMemo(
    () => (post) => {
      if (post.author?.id && profile?.id && post.author.id === profile.id) {
        return true;
      }
      return userRole === "owner" || userRole === "admin";
    },
    [profile?.id, userRole],
  );

  const [club, setClub] = useState(null);
  const [branches, setBranches] = useState([]);
  const [clubLoading, setClubLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [posts, setPosts] = useState([]);
  const [category, setCategory] = useState("all");
  const [loading, setLoading] = useState(true);
  const [bioExpanded, setBioExpanded] = useState(false);

  const { theme, shellStyle, heroStyle } = useClubPortalBranding("aa", club || authTenant, false);

  const { commentBusy, openComments, commentsByPost, toggleLike, loadComments, addComment } =
    useClubLifeInteractions(setPosts, { commentsApi: "admin" });

  const loadClub = useCallback(async () => {
    try {
      const [settingsRes, branchesRes] = await Promise.all([
        clinicAPI.getTenantSettings(),
        clinicAPI.getBranches().catch(() => ({ branches: [] })),
      ]);
      const tenant = settingsRes.tenant || authTenant;
      setClub({
        id: tenant.id,
        name: tenant.name,
        phone: tenant.phone,
        address: tenant.address,
        logo_url: tenant.logo_url,
        portal_cover_url: tenant.portal_cover_url,
        description: tenant.portal_description || null,
        portal_gallery: tenant.portal_gallery,
        members_count: tenant.members_count ?? null,
      });
      setBranches(
        (branchesRes.branches || []).filter((b) => b.is_active !== false),
      );
    } catch {
      setClub(
        authTenant
          ? {
              id: authTenant.id,
              name: authTenant.name,
              phone: authTenant.phone,
              address: authTenant.address,
              logo_url: authTenant.logo_url,
              portal_cover_url: authTenant.portal_cover_url,
              description: authTenant.portal_description || null,
              portal_gallery: authTenant.portal_gallery,
            }
          : null,
      );
      setBranches([]);
    } finally {
      setClubLoading(false);
    }
  }, [authTenant]);

  const load = useCallback(async () => {
    try {
      const data = await clinicAPI.getClubLifePosts({
        category: category === "all" ? undefined : category,
      });
      setPosts(data.posts || []);
    } catch (e) {
      toast.error(e.response?.data?.error || "Ошибка загрузки");
      setPosts([]);
    } finally {
      setLoading(false);
    }
  }, [category, toast]);

  useEffect(() => {
    loadClub();
  }, [loadClub]);

  useEffect(() => {
    setLoading(true);
    load();
  }, [load]);

  useAdminRefresh(() => {
    loadClub();
    load();
  });

  const handleTenantUpdated = (next) => {
    setClub((prev) => ({
      ...(prev || {}),
      id: next.id,
      name: next.name,
      phone: next.phone,
      address: next.address,
      logo_url: next.logo_url,
      portal_cover_url: next.portal_cover_url,
      description: next.portal_description || null,
      portal_gallery: next.portal_gallery,
      members_count: prev?.members_count ?? null,
    }));
    dispatch(updateTenant(next));
  };

  const clubName = club?.name || authTenant?.name || "Клуб";
  const logoUrl = club?.logo_url || authTenant?.logo_url || null;
  const photos = useMemo(() => {
    if (club) return clubGalleryPhotos(club);
    return clubGalleryPhotos({
      logo_url: authTenant?.logo_url,
      portal_gallery: authTenant?.portal_gallery,
    });
  }, [club, authTenant]);

  const bannerPhotos = useMemo(() => {
    if (photos.length) return photos;
    const cover = club?.portal_cover_url || authTenant?.portal_cover_url;
    return cover ? [cover] : [];
  }, [photos, club, authTenant]);

  const description = club?.description || authTenant?.portal_description || null;
  const phone = club?.phone || authTenant?.phone || null;
  const address = club?.address || authTenant?.address || null;
  const extraBranches = branches.filter(
    (b) => b.address && b.address !== address,
  );
  const accentColor = theme?.accent || "var(--ca-accent)";
  const hasContacts = address || phone || extraBranches.length > 0;
  const bioPreview =
    description && description.length > 120 && !bioExpanded
      ? `${description.slice(0, 120).trim()}…`
      : description;

  const editTenant = useMemo(() => {
    if (!club && !authTenant) return null;
    return {
      ...(authTenant || {}),
      ...(club || {}),
      portal_description:
        club?.description ?? authTenant?.portal_description ?? "",
    };
  }, [club, authTenant]);

  const remove = async (post) => {
    if (!window.confirm("Удалить публикацию?")) return;
    try {
      await clinicAPI.deleteClubLifePost(post.id);
      toast.success("Удалено");
      load();
    } catch (e) {
      toast.error(e.response?.data?.error || "Ошибка");
    }
  };

  return (
    <>
      <div
        className={`${s.clubPage} clubPage ${desktop ? md.desktopClubPage : ""}`}
        style={shellStyle}
      >
        {clubLoading ? (
          <Skeleton height={320} style={{ borderRadius: desktop ? 10 : 0 }} />
        ) : (
          <div className={desktop ? md.desktopClubHeader : s.clubVkHeader}>
            <section className={s.clubVkCover} aria-label="О клубе">
              <div className={aa.adminClubGalleryWrap}>
                <ClubPhotoSlider
                  photos={bannerPhotos}
                  accentColor={accentColor}
                  galleryClassName={s.clubCoverGallery}
                  emptyClassName={`${s.clubGalleryEmpty} ${s.clubCoverGalleryEmpty}`}
                  emptyStyle={heroStyle}
                  hideDots
                />
                <button
                  type="button"
                  className={aa.adminClubEditBtn}
                  onClick={() => setEditOpen(true)}
                >
                  Редактировать
                </button>
              </div>
            </section>

            <header className={s.clubVkProfile}>
              <div className={s.clubVkTopRow}>
                <div className={s.clubVkAvatarSlot}>
                  {logoUrl ? (
                    <Avatar
                      name={clubName}
                      src={mediaUrl(logoUrl)}
                      size="lg"
                      className={s.clubVkAvatar}
                    />
                  ) : (
                    <div className={s.clubVkAvatarFallback} aria-hidden>
                      {clubName.charAt(0).toUpperCase()}
                    </div>
                  )}
                </div>
                <div className={s.clubVkTitleBlock}>
                  <h1 className={s.clubVkName}>{clubName}</h1>
                  <p className={s.clubVkSubline}>
                    <strong>{posts.length}</strong> в ленте ·{" "}
                    <strong>{photos.length}</strong> фото
                    {club?.members_count != null ? (
                      <>
                        {" "}
                        · {membersCountLabel(club.members_count)}
                      </>
                    ) : null}
                  </p>
                </div>
              </div>

              {description ? (
                <div className={s.clubVkBioBlock}>
                  <p className={s.clubVkBio}>{bioPreview}</p>
                  {description.length > 120 ? (
                    <button
                      type="button"
                      className={s.clubVkBioMore}
                      onClick={() => setBioExpanded((v) => !v)}
                    >
                      {bioExpanded ? "Свернуть" : "Ещё"}
                    </button>
                  ) : null}
                </div>
              ) : null}

              {hasContacts ? (
                <ul className={s.clubVkMetaList}>
                  {address ? (
                    <li className={s.clubVkMetaItem}>
                      <PinIcon />
                      <span>{address}</span>
                    </li>
                  ) : null}
                  {extraBranches.map((b) => (
                    <li key={b.id} className={s.clubVkMetaItem}>
                      <PinIcon />
                      <span>
                        {b.name}
                        {formatBranchLine(b) ? ` · ${formatBranchLine(b)}` : ""}
                      </span>
                    </li>
                  ))}
                  {phone ? (
                    <li className={s.clubVkMetaItem}>
                      <PhoneIcon />
                      <a
                        className={s.clubVkMetaLink}
                        href={`tel:${phone.replace(/\s/g, "")}`}
                      >
                        {phone}
                      </a>
                    </li>
                  ) : null}
                </ul>
              ) : null}

              <div className={s.clubVkActions}>
                <Link to="/manage/app/chat/club" className={s.clubVkBtnMessage}>
                  <ChatIcon />
                  <span>Чат клуба</span>
                </Link>
                <Link to="/manage/app/clients" className={s.clubVkBtnMuted}>
                  <UsersIcon />
                  <span>Участники</span>
                </Link>
              </div>
            </header>
          </div>
        )}

        <section
          className={`${s.clubFeedSection} ${desktop ? md.desktopClubFeedSection : ""}`}
          aria-label="Лента клуба"
        >
          <div className={`${s.clubFeedStickyBar} ${desktop ? md.desktopClubFeedBar : ""}`}>
            <h2 className={s.clubFeedTitle}>Лента</h2>
            <nav className={s.clubFeedTabs} aria-label="Категории">
              {FILTERS.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  className={
                    category === c.id ? s.clubFeedTabOn : s.clubFeedTab
                  }
                  onClick={() => setCategory(c.id)}
                >
                  <span className={s.clubFeedTabIcon} aria-hidden>
                    {c.icon}
                  </span>
                  {c.label}
                </button>
              ))}
            </nav>
          </div>

          <div className={`${s.clubComposerSlot} ${desktop ? md.desktopClubComposer : ""}`}>
            <ClubLifeComposer compact variant="admin" onPublished={load} />
          </div>

          {loading ? (
            <div className={s.clubFeedSkeleton}>
              <Skeleton height={200} style={{ borderRadius: 16 }} />
            </div>
          ) : posts.length === 0 ? (
            <div className={s.clubFeedEmpty}>
              <div className={s.clubFeedEmptyIcon} aria-hidden>
                ✨
              </div>
              <p className={s.clubFeedEmptyTitle}>Пока тихо</p>
              <p className={s.clubFeedEmptyHint}>
                Будьте первым — поделитесь новостью, фото или достижением
              </p>
            </div>
          ) : (
            <div className={`${s.clubFeedList} ${desktop ? md.desktopClubFeedList : ""}`}>
              {posts.map((post) => (
                <ClubLifePostCard
                  key={post.id}
                  post={post}
                  mode="admin"
                  onDelete={remove}
                  canDeletePost={canDeletePost}
                  onLike={toggleLike}
                  onLoadComments={loadComments}
                  commentsOpen={openComments === post.id}
                  comments={commentsByPost[post.id] || []}
                  onComment={addComment}
                  commentBusy={commentBusy}
                />
              ))}
            </div>
          )}
        </section>
      </div>

      <AdminClubEditSheet
        open={editOpen}
        tenant={editTenant}
        onClose={() => setEditOpen(false)}
        onUpdated={handleTenantUpdated}
      />
    </>
  );
}
